import { isNativeApp } from '../utils/platform';
import { post, del } from '../utils/api';

/**
 * Remote push notifications (APNs via @capacitor/push-notifications).
 * Lazy-imports the plugin so web builds don't pull in native code.
 * Follows the thenable-safe pattern: split async loader (Promise<void>) from
 * sync access to the cached module reference.
 */

let _PushNotifications: typeof import('@capacitor/push-notifications').PushNotifications | null = null;
let listenersRegistered = false;
let currentToken: string | null = null;

async function loadPushPlugin(): Promise<void> {
  if (!_PushNotifications) {
    const mod = await import('@capacitor/push-notifications');
    _PushNotifications = mod.PushNotifications;
  }
}

const BUNDLE_ID = 'com.wallstreetle.app';

function getEnvironment(): 'development' | 'production' {
  // Vite/CRA expose env via process.env. The aps-environment in App.entitlements
  // controls APNs sandbox vs prod from iOS's side; we mirror it here so the
  // backend can pick the right APNs Provider connection.
  return process.env.NODE_ENV === 'production' ? 'production' : 'development';
}

export async function checkPushPermissions(): Promise<'granted' | 'denied' | 'prompt'> {
  if (!isNativeApp()) return 'denied';
  try {
    await loadPushPlugin();
    const result = await _PushNotifications!.checkPermissions();
    return result.receive as 'granted' | 'denied' | 'prompt';
  } catch {
    return 'denied';
  }
}

export async function requestPushPermissions(): Promise<boolean> {
  if (!isNativeApp()) return false;
  try {
    await loadPushPlugin();
    const result = await _PushNotifications!.requestPermissions();
    return result.receive === 'granted';
  } catch {
    return false;
  }
}

async function postTokenToBackend(token: string): Promise<void> {
  try {
    await post('/api/push/register-token', {
      token,
      platform: 'ios',
      bundleId: BUNDLE_ID,
      environment: getEnvironment(),
    });
    currentToken = token;
  } catch (err) {
    console.error('[Push] Failed to register token with backend', err);
  }
}

async function registerListeners(): Promise<void> {
  if (listenersRegistered) return;
  await loadPushPlugin();

  _PushNotifications!.addListener('registration', async (token) => {
    await postTokenToBackend(token.value);
  });

  _PushNotifications!.addListener('registrationError', (err) => {
    console.error('[Push] Registration error', err);
  });

  _PushNotifications!.addListener('pushNotificationReceived', (notification) => {
    // Foreground delivery — iOS shows banner via presentationOptions in capacitor.config.
    console.info('[Push] Received in foreground', notification);
  });

  _PushNotifications!.addListener('pushNotificationActionPerformed', (action) => {
    // User tapped — iOS already brought us to foreground. Optional deep link via data.deepLink.
    const deepLink = (action.notification.data as Record<string, string> | undefined)?.deepLink;
    if (deepLink && typeof deepLink === 'string') {
      try {
        window.location.assign(deepLink);
      } catch {
        // ignore
      }
    }
  });

  listenersRegistered = true;
}

/**
 * Boot-time init. Safe to call multiple times. Only registers with APNs if the
 * user has previously granted permission — never auto-prompts.
 */
export async function initializePushNotifications(): Promise<void> {
  if (!isNativeApp()) return;
  try {
    await loadPushPlugin();
    const status = await _PushNotifications!.checkPermissions();
    if (status.receive !== 'granted') return;
    await registerListeners();
    await _PushNotifications!.register();
  } catch (err) {
    console.error('[Push] Initialize failed', err);
  }
}

/**
 * User-initiated enable: request permission, then register.
 */
export async function enablePushNotifications(): Promise<boolean> {
  if (!isNativeApp()) return false;
  try {
    const granted = await requestPushPermissions();
    if (!granted) return false;
    await registerListeners();
    await loadPushPlugin();
    await _PushNotifications!.register();
    return true;
  } catch (err) {
    console.error('[Push] Enable failed', err);
    return false;
  }
}

/**
 * Best-effort token removal on sign-out. Backend will also prune invalid tokens
 * lazily on send failure.
 */
export async function unregisterPushTokenFromBackend(): Promise<void> {
  if (!currentToken) return;
  try {
    await del('/api/push/register-token', { data: { token: currentToken } });
  } catch (err) {
    console.error('[Push] Failed to unregister token', err);
  } finally {
    currentToken = null;
  }
}
